import CodeBlock from '../components/CodeBlock';

export default function BatchProcessing() {
  return (
    <section id="batch" className="mb-16">
      <h3 className="text-2xl font-bold text-gray-900 mb-4">POST /screenshot/batch</h3>

      <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-blue-900">Pro Plan Exclusive</p>
            <p className="text-sm text-blue-800">
              Batch processing is only available on the Pro plan. Process up to 10 URLs in a single API request
              with extended timeouts (120 seconds) for reliable batch execution.
            </p>
          </div>
        </div>
      </div>

      <p className="text-gray-700 mb-6">
        Process multiple website screenshots simultaneously. The API supports two response formats:
        JSON with base64-encoded images (default) or a ZIP file download. Each screenshot
        counts towards your monthly quota.
      </p>

      <h4 className="text-lg font-semibold text-gray-900 mb-3">Option 1: JSON Response (Default)</h4>
      <p className="text-gray-700 mb-3">
        Returns base64-encoded images in a JSON response with individual success/failure tracking for each URL.
        Best for programmatic processing and API integration.
      </p>
      <CodeBlock section="batch" />

      <div className="mt-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-3">JSON Response Format</h4>
        <p className="text-gray-700 mb-3">
          The API returns a JSON object containing base64-encoded images with generated filenames:
        </p>
        <CodeBlock section="batchResponse" />
      </div>

      <div className="mt-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-3">Option 2: ZIP File Download</h4>
        <p className="text-gray-700 mb-3">
          Add <code className="bg-gray-100 px-2 py-1 rounded text-sm">?responseFormat=zip</code> to download
          all screenshots as a single ZIP file. Perfect for bulk downloads and manual processing.
        </p>
        <CodeBlock section="batchZip" />
        <div className="mt-3 bg-blue-50 border-l-4 border-blue-500 p-4">
          <p className="text-sm text-blue-900">
            <strong>💡 ZIP Format Benefits:</strong> The ZIP file includes all screenshots with descriptive
            filenames (domain-timestamp.format) and a README.txt listing any failed URLs.
          </p>
        </div>
      </div>

      <div className="mt-6 bg-gray-50 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Batch Limits</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between border-b border-gray-200 pb-3">
            <span className="text-gray-700">Free / Starter / Growth</span>
            <span className="font-semibold text-gray-500">Not Available</span>
          </div>
          <div className="flex items-center justify-between pb-3">
            <span className="text-gray-700">Pro</span>
            <span className="font-semibold text-blue-600">Up to 10 URLs per request</span>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-4">
          💡 <strong>Tip:</strong> Each URL in the batch counts as one request toward your monthly quota.
          A batch of 10 URLs = 10 requests deducted.
        </p>
      </div>

      <div className="mt-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-3">Advanced Batch with Options</h4>
        <p className="text-gray-700 mb-3">
          All standard screenshot options (format, fullPage, viewport, etc.) can be applied to batch requests:
        </p>
        <CodeBlock section="batchAdvanced" />
      </div>

      <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-yellow-900">Best Practices</p>
            <ul className="text-sm text-yellow-800 list-disc list-inside mt-1 space-y-1">
              <li>Use batch processing for related screenshots to reduce API overhead</li>
              <li>Handle individual URL failures gracefully in your application</li>
              <li>Consider processing large batches in chunks to avoid timeouts</li>
              <li>Pro plan users get extended timeouts (120s) for better batch reliability</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
